let axiosClient;
import { setCookie } from '/src/common/cookie-utils';
import { useLoadingIndicator } from '#app/composables/loading-indicator';
import $axios from 'axios';
import VueAxios from 'vue-axios';
import { toast } from './toaster.client';
import { generateRequestId } from '../src/common/general-utils.js';

export default defineNuxtPlugin((nuxtApp) => {
  // TODO: enable vuex here
  const { store } = nuxtApp;
  const redirect = useRouter().push;
  const route = useRoute();

  let responseCount = 0;

  let pendingRequests = 0;
  let { isLoading, start, finish, clear, error } = useLoadingIndicator({
    throttle: 0
  });
  function handleNuxtLoadingProgress() {
    pendingRequests--;
    if (pendingRequests === 0) {
      finish();
      error._value = false;
    }
    if (pendingRequests && !isLoading.value) {
      start();
    }
  }

  // This is where we can do further axios config and set up interceptors
  // for errors. FOr more info see https://axios.nuxtjs.org/helpers.html
  $axios.interceptors.response.use(
    (response) => {
      handleNuxtLoadingProgress();
      /*
        Unhandled api calls come through as an HTML page.
        This happens because nuxt is the last handler and it handles all
        unhandled calls, including "/api/*".
      */
      if (response.headers['maxage']) {
        //calculate expected session end time so that idle dialog could be displayed to user
        setCookie('sessionTimeout', new Date().getTime() + Number(response.headers['maxage']));
      }
      if (response.headers['session-expired']) {
        if (responseCount === 0) {
          window.location = '/login?reason=invalidsession';
        }
        responseCount++;
        return Promise.resolve(response.data);
      }

      if (response.headers['saml-logged-out'] && route.path !== '/login') {
        if (responseCount === 0) {
          window.location = '/login?isSamlUser=1';
        }
        responseCount++;
        return Promise.resolve(response.data);
      }

      if (response.config.url === '/api/users/forceLogoutCheck' && response.data !== 'Authorized') {
        if (
          route.path !== '/login' &&
          !route.path.startsWith('/verify-otp') &&
          !route.path.startsWith('/form') &&
          !route.path.startsWith('/reset-password') &&
          !route.path.startsWith('/forgot-password')
        ) {
          window.location = '/login';
        }
        return Promise.resolve(response.data);
      }
      checkLoginRedir(response);
      if (response.config?.resolveResponse) {
        return Promise.resolve(response);
      }
      return Promise.resolve(response.data);
    },
    (er) => {
      // Silently reject cancelled requests without triggering the red error loading bar.
      if ($axios.isCancel(er) || er.message === 'canceled') {
        // Restart the loading indicator instantly if other requests are still pending, bypassing the fade-out delay.
        if (pendingRequests > 1) {
          clear();
          start();
        }
        handleNuxtLoadingProgress();
        return Promise.reject(er);
      }

      error._value = true;
      handleNuxtLoadingProgress();
      if (!axios.isAxiosError(er)) {
        return Promise.reject(er);
      }
      if (er + '' === 'Error: Network Error') {
        toast.error(er, 10000);
        return Promise.reject(er);
      }

      if (er.response && er.response.status === 401) {
        if (route.path.startsWith('/form')) {
          if (
            er.config?.url === '/api/users/forceLogoutCheck' ||
            er.response?.config?.url === '/api/users/forceLogoutCheck'
          ) {
            return Promise.reject(er);
          }
          store.commit('external-forms/setInvalid', true);
          return Promise.reject(er);
        } else if (er.response.data && er.response.data.message === 'ForceLogOut') {
          return redirect('/admin/force-logout');
        }

        if (
          !route.path.startsWith('/login') &&
          !route.path.startsWith('/verify-otp') &&
          !route.path.startsWith('/forgot-password') &&
          !route.path.startsWith('/reset-password')
        ) {
          redirect('/not-authorized');
          return Promise.reject(er);
        }
      }
      if (er.code === 500) {
        // Set state on the store to indicate that a server
        // side error has occurred. Then, watchers of that state
        // can do something about this error.
        toast.error(er, 10000);
      }
      const errorCode = er.response?.status || er.code;
      if (typeof errorCode === 'number' && errorCode > 500) {
        const customError = new Error(
          'An unexpected server error has occurred. "We apologize for the inconvenience. Please try again later, and contact Sofy support team if the issue persists.'
        );
        customError.response = er.response;
        customError.stack = er.stack;
        return Promise.reject(customError);
      }
      return Promise.reject(er);
    }
  );

  $axios.interceptors.request.use((config) => {
    config.headers['request-id'] = config.headers['request-id'] || generateRequestId();
    // Start the loading indicator when requests start
    if (pendingRequests === 0) {
      start();
    }
    error._value = false;
    pendingRequests++;
    config.params = { _t: new Date().getTime(), ...config.params };
    return config;
  });

  axiosClient = $axios;
  nuxtApp.vueApp.use(VueAxios, { $axios });
});

const checkLoginRedir = (response, toast) => {
  /* Caution: Do not depend on `responseURL` for redirection in ie11. This is not supported */
  if (
    typeof response.data === 'string' &&
    response.data.indexOf('<!doctype html>') === 0 &&
    response.request.responseURL
  ) {
    if (
      response.request.responseURL.indexOf(`${location.origin}/login`) > -1 ||
      response.request.responseURL.includes('/verify-otp')
    ) {
      window.location = response.request.responseURL;
    } else {
      toast.error(`No such API: ${response.request.responseURL}`, 10000);
    }
  }
};

/**
 * Export as service when $axios is set
 * Reference: https://github.com/nuxt-community/axios-module/issues/28
 */
export const axios = [
  '$request',
  '$delete',
  '$get',
  '$head',
  '$options',
  '$post',
  '$put',
  '$patch',
  'isAxiosError'
].reduce((acc, method) => {
  acc[method] = function () {
    if (!axiosClient) throw new Error('Nuxt Axios not yet loaded');
    //remove $ from all http call names. This was a nuxt syntax not a raw axios one
    return axiosClient[method.replace(/^\$/, '')].apply(null, arguments);
  };
  return acc;
}, {});
